import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Button } from 'foundations-components/transitional/components/Button';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { SelectableBox } from 'foundations-components/transitional/components/SelectableBox';
import { View } from 'foundations-components/transitional/components/View';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import { useUsageTracker } from 'marketplace-ui-tracking';
import { useCallback, useMemo, useState } from 'react';
import { MonetizationStatusAlert } from 'usage-based-billing-components-lib/components';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { PricingPlan } from '../../pricing/PricingPlan';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function ProductPricingCard({
  agencyPricingUrl,
  currency,
  pricingPlansUrl,
  pricingPlans,
  ubbInfo,
  pricingUpdatedAt
}) {
  const {
    appContext,
    name,
    productType,
    trackInteraction
  } = useDetailsPageContext();
  const isCustomer = appContext === 'app';
  const usageTracker = useUsageTracker();
  const [selectedPricingIndex, setSelectedPricingIndex] = useState(0);
  const genericPricingInfo = useMemo(() => {
    if (productType === 'AGENT' && !ubbInfo) {
      return [];
    }
    if (productType === 'AGENT' && ubbInfo) {
      const costDetails = ubbInfo.costDetails || [];
      const plansFromCosts = costDetails.map(({
        label,
        cost
      }) => ({
        pricingModel: ['AGENT'],
        pricingName: label,
        pricingMonthlyCenticents: (typeof cost === 'number' ? cost : 0) * 10000
      }));
      return plansFromCosts;
    }
    return pricingPlans;
  }, [productType, ubbInfo, pricingPlans]);
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product pricing card'
    }, eventProps));
  }, [trackInteraction]);
  const renderedPricingCardBody = useMemo(() => {
    if (productType === 'AGENT' && !genericPricingInfo.length) {
      return /*#__PURE__*/_jsx(MonetizationStatusAlert, {
        usageTracker: usageTracker,
        screen: "marketplaceListing"
      });
    }
    if (genericPricingInfo.length > 1) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(View, {
          marginBlockEnd: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `detailsPage.pricingCard.subTitle.${productType}`,
            options: {
              name
            }
          })
        }), /*#__PURE__*/_jsxs(Grid, {
          children: [/*#__PURE__*/_jsx(GridItem, {
            size: {
              xs: 12,
              lg: 6
            },
            children: genericPricingInfo.map((pricing, index) => /*#__PURE__*/_jsxs("div", {
              children: [/*#__PURE__*/_jsx(SelectableBox, {
                selected: selectedPricingIndex === index,
                onClick: () => setSelectedPricingIndex(index),
                type: "default",
                marginBlockEnd: "200",
                inlineSize: "100%",
                children: /*#__PURE__*/_jsxs(Flex, {
                  flexDirection: "column",
                  justifyContent: "start",
                  alignItems: "start",
                  children: [/*#__PURE__*/_jsxs(Flex, {
                    justifyContent: "space-between",
                    alignItems: "center",
                    inlineSize: "100%",
                    children: [/*#__PURE__*/_jsx(Text, {
                      as: "strong",
                      textAlign: "left",
                      children: pricing.pricingName
                    }), pricing.pricingFreeTrialDays ? /*#__PURE__*/_jsx(Badge, {
                      use: "koala",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: `detailsPage.pricingCard.plan.freeTrialBadgeText`,
                        options: {
                          freeTrialDays: pricing.pricingFreeTrialDays
                        }
                      })
                    }) : null]
                  }), pricing.pricingTagline ? /*#__PURE__*/_jsx(Text, {
                    as: "small",
                    textAlign: "left",
                    children: pricing.pricingTagline
                  }) : null]
                })
              }), selectedPricingIndex === index && /*#__PURE__*/_jsx(MediaQueryView, {
                max: "md",
                children: /*#__PURE__*/_jsx(PricingPlan, {
                  currency: currency,
                  pricing: genericPricingInfo[selectedPricingIndex],
                  onTrackInteraction: handleTrackInteraction
                })
              })]
            }, `pricing-plan-${index}`))
          }), /*#__PURE__*/_jsx(GridItem, {
            size: {
              md: 12,
              lg: 6
            },
            inlineSize: "100%",
            children: /*#__PURE__*/_jsx(MediaQueryView, {
              min: "md",
              children: /*#__PURE__*/_jsx(PricingPlan, {
                currency: currency,
                pricing: genericPricingInfo[selectedPricingIndex],
                onTrackInteraction: handleTrackInteraction
              })
            })
          })]
        }), productType === 'APP' && /*#__PURE__*/_jsx(View, {
          marginBlockStart: "600",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "detail-200",
            color: "text-core-subtle",
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "detailsPage.pricingCard.disclaimerText_jsx",
              elements: {
                b: 'b',
                Date: FormattedDateTime
              },
              options: {
                format: 'L',
                type: isCustomer ? 'portalTz' : 'utc',
                value: pricingUpdatedAt
              }
            })
          })
        })]
      });
    } else if (genericPricingInfo.length === 1) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(MediaQueryView, {
          min: "lg",
          children: [productType === 'AGENT' && /*#__PURE__*/_jsx(View, {
            marginBlockEnd: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `detailsPage.pricingCard.subTitle.${productType}`,
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsx(Flex, {
            inlineSize: "fit-content",
            justifyContent: "start",
            minInlineSize: "50%",
            children: /*#__PURE__*/_jsx(PricingPlan, {
              currency: currency,
              pricing: genericPricingInfo[selectedPricingIndex],
              onTrackInteraction: handleTrackInteraction
            })
          })]
        }), /*#__PURE__*/_jsxs(MediaQueryView, {
          max: "lg",
          children: [productType === 'AGENT' && /*#__PURE__*/_jsx(View, {
            marginBlockEnd: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `detailsPage.pricingCard.subTitle.${productType}`,
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsx(Flex, {
            inlineSize: "fit-content",
            justifyContent: "start",
            minInlineSize: "100%",
            children: /*#__PURE__*/_jsx(PricingPlan, {
              currency: currency,
              pricing: genericPricingInfo[selectedPricingIndex],
              onTrackInteraction: handleTrackInteraction
            })
          })]
        }), productType === 'APP' && /*#__PURE__*/_jsx(View, {
          marginBlockStart: "600",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "detail-200",
            color: "text-core-subtle",
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "detailsPage.pricingCard.disclaimerText_jsx",
              elements: {
                b: 'b',
                Date: FormattedDateTime
              },
              options: {
                format: 'L',
                type: isCustomer ? 'portalTz' : 'utc',
                value: pricingUpdatedAt
              }
            })
          })
        })]
      });
    } else {
      return null;
    }
  }, [productType, genericPricingInfo, usageTracker, name, currency, selectedPricingIndex, handleTrackInteraction, isCustomer, pricingUpdatedAt]);
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    id: "details-page-pricing",
    name: "pricing card",
    title: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "200",
      alignItems: "start",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.pricingCard.title"
      })
    }),
    actions: /*#__PURE__*/_jsxs("div", {
      children: [agencyPricingUrl && /*#__PURE__*/_jsx(Button, {
        use: "link",
        external: true,
        href: agencyPricingUrl,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.pricingCard.agencyPricingLinkText"
        })
      }), pricingPlansUrl && /*#__PURE__*/_jsx(Button, {
        use: "link",
        external: true,
        href: pricingPlansUrl,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.pricingCard.pricingLinkText"
        })
      }), productType === 'AGENT' && !!genericPricingInfo.length && /*#__PURE__*/_jsx(Button, {
        use: "link",
        external: true,
        href: "https://legal.hubspot.com/hubspot-product-and-services-catalog#HubSpotCreditsRateSheet",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.pricingCard.creditsRateSheetLinkText"
        })
      })]
    }),
    children: renderedPricingCardBody
  });
}